"use client";

import { useQuery } from "@tanstack/react-query";

import { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { isSuperAdmin, useSession } from "@punchup/data/legacy";

/**
 * useIsSuperAdmin Usage:
 * Returns if the user is a super admin
 *
 * @returns
 * - undefined if still loading
 * - boolean if the user is a super admin
 *
 * Backed by React Query keyed on the user id, so every mount across the app
 * (e.g. Navbar, the command palette, UserRolesProvider) shares a single
 * `super_admins` lookup instead of each firing its own query.
 */
export default function useIsSuperAdmin() {
  const supabase = createClientComponentClient<Database>();
  const session = useSession();
  const userId = session?.user?.id ?? null;

  const { data, isError } = useQuery({
    queryKey: ["is-super-admin", userId],
    queryFn: () => isSuperAdmin(supabase),
    // Wait until the session has resolved (undefined = still loading).
    enabled: session !== undefined,
    // Preserve the original single-attempt-then-false behaviour on error.
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  if (isError) {
    return false;
  }
  return data;
}
